import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Tabs } from 'antd';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import {
	ApprovedTaskFrom,
	AutoApprovedTaskFrom,
	PendingTaskFrom,
	RejectedTrips,
	PODTaskForm,
	PoRTaskForm,
} from 'components/forms/approval';
import { ACL } from 'components/common';
import { useAuth } from 'hooks';

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
	padding: 24px;
`;

const HeaderContainer = styled(Box)`
	margin-bottom: 24px;
`;

const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #333;
	display: block;
	margin-bottom: 4px;
`;

const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
	display: block;
`;

const TabsContainer = styled(Box)`
	margin-top: 24px;
`;

const StyledTabs = styled(Tabs)`
	.ant-tabs-tab {
		font-size: 16px;
		font-weight: 500;
		padding: 12px 24px;
	}

	.ant-tabs-tab-active {
		.ant-tabs-tab-btn {
			color: #1890ff;
			font-weight: 600;
		}
	}

	.ant-tabs-ink-bar {
		background: #1890ff;
	}
`;

const ContentContainer = styled(Box)`
	margin-top: 24px;
`;

export default function TripAndPODApprovalPageV2() {
	const [activeTab, setActiveTab] = useState(1);
	const [searchParams, setSearchParams] = useSearchParams();
	const { permissions } = useAuth();

	const onChange = useCallback(
		(key) => {
			const newQueryParameters = new URLSearchParams();
			setSearchParams({
				...newQueryParameters,
				tab: key,
			});

			setActiveTab(Number(key));
		},
		[setSearchParams]
	);

	useEffect(() => {
		const tab = Number(searchParams.get('tab'));
		if (tab) {
			onChange(tab);
		}
	}, [onChange, searchParams]);

	const canViewPOD =
		permissions?.search?.includes('proof_of_delivery') || permissions?.search?.includes('all');

	const canViewPoR =
		permissions?.search?.includes('proof_of_request') ||
		permissions?.search?.includes('proof_of_delivery') ||
		permissions?.search?.includes('all');

	const tabs = useMemo(
		() => [
			{
				id: 1,
				label: 'Pending',
				form: <PendingTaskFrom tab={activeTab} />,
			},
			{
				id: 2,
				label: 'Approved',
				form: <ApprovedTaskFrom tab={activeTab} />,
			},
			{
				id: 3,
				label: 'Auto Approved',
				form: <AutoApprovedTaskFrom tab={activeTab} />,
			},
			{
				id: 4,
				label: 'Reject',
				form: <RejectedTrips tab={activeTab} />,
			},
			...(canViewPOD
				? [
						{
							id: 5,
							label: 'Proof of Delivery',
							form: (
								<ACL action="search" module="proof_of_delivery">
									<PODTaskForm tab={activeTab} />
								</ACL>
							),
						},
				  ]
				: []),
			...(canViewPoR
				? [
						{
							id: 6,
							label: 'Proof of Request',
							form: <PoRTaskForm tab={activeTab} />,
						},
				  ]
				: []),
		],
		[activeTab, canViewPOD, canViewPoR]
	);

	return (
		<PageContainer>
			<HeaderContainer>
				<PageTitle>Trip and POD Approvals</PageTitle>
				<PageSubtitle>Manage trip and proof of delivery approvals</PageSubtitle>
			</HeaderContainer>

			<TabsContainer>
				<StyledTabs
					onChange={onChange}
					tabPosition="top"
					type="line"
					activeKey={activeTab}
					defaultActiveKey={1}
					destroyInactiveTabPane
					items={tabs.map((tab) => ({
						label: `${tab.label}`,
						key: tab.id,
						children: <ContentContainer>{tab.form}</ContentContainer>,
					}))}
				/>
			</TabsContainer>
		</PageContainer>
	);
}
